import * as React from "react";
import { Subscriber } from 'react-broadcast';

import { EntityType, Vehicle, Key } from "../../../../models";

import { Container } from "../../../Container";
import { addNewRouteFor, editRouteFor, vehicleGroupActionsRoute, appNavigator } from "../../../routes";

import { EditCreateEntityRoute, EditCreateEntityProps } from "../EditCreate/EditCreateEntityRoute";
import { ListStore } from "../../../flux/Grid/ListStore";
import { SelectedItemsStore } from "../../../flux/Grid/SelectedItemsStore";
import { SelectedItemsActions } from "../../../flux/Grid/SelectedItemsActions";

import { NewVehicle } from "./NewVehicle";
import { VehicleGrid } from "./VehicleGrid";
import { EditVehicleStore, UpsertVehicleController, EditVehicleActions } from "./flux";
import { TimezoneStore } from "../../../utils/TimeZone/TimeZoneStore";
import { VehicleBatchEdit } from "./BatchEdit/BatchEdit";

import { BatchAction, ActionType, VehicleBatchEditActions, VehicleBatchEditStore } from "./BatchEdit/flux";
import { Button } from "../../../../components/Buttons";
import { EditCreateEntityActions } from "../../../actions/EditCreateEntityActions";
import { ErrorStore } from "../../../flux/Error/ErrorStore";
import { FlagStore } from "../../../flux/Flag/FlagStore";
import { Toolbar } from "../../Toolbar";
import { Modal } from "../../../../components/Modal/Modal";
import { OneButtonDialog } from "../../Dialogs/OneButtonDialog";
import { FlagActions } from "../../../flux/Flag/FlagActions";
import { StatBuilder } from "../../../../utils/StatBuilder";
import { SearchProps } from "../SearchProps";
import { twoButtonDialogConfig } from "../../../utils/configure-store";
import { ErrorMessage } from "../../../../components/ErrorMessage";
import { NoResults } from "../../Table/NoResults";
import { TableTitle } from "../../Table/TableTitle";
import { LabelStore } from "../../../flux/Label/LabelStore";
import { Polyglot } from "../../../../polyglot";
import { StickyMediator } from "../../../utils/StickyMediator";
import { ValueStore } from "../../../flux/Value/ValueStore";
import { GenericSearchWidget } from "../Search/GenericSearchWidget";
import { TrashButtonWidget } from "../../TrashButtonWidget";
import { DeleteItemsProps, DeleteConfirmationWidget } from "../../DeleteConfirmationWidget";
import { ShowVehicleGroupActionsCommand, ShowVehicleGeopositionCommand, SelectGroupActionCommand, ApplyVehicleGroupActionsCommand } from "./commands";
import { Command2 } from "../../../../Command";
import { NonIsolatedPremise } from "../../NonIsolatedPremise";

const appStyles = require('../../../app.scss');
const tableStyles = require('../../../../components/Table.scss');

interface TabProps
{
    polyglot: Polyglot;
    isAdmin: boolean;

    tableTitleStore: LabelStore;
    listStore: ListStore<Vehicle>;
    selectedItemsStore: SelectedItemsStore<Vehicle>;
    timezoneStore: TimezoneStore;
    toolbarErrorStore: ErrorStore;

    alertVisibilityStore: FlagStore;
    mapInProgressStore: FlagStore;
    showVehicleGeopositionCommand: ShowVehicleGeopositionCommand;

    groupEditStore: VehicleBatchEditStore;
    showVehicleGroupActionsCommand: ShowVehicleGroupActionsCommand;
    selectGroupActionCommand: SelectGroupActionCommand;
    applyGroupActionCommand: ApplyVehicleGroupActionsCommand;
    applyInProgressStore: FlagStore;
    applyErrorStore: ErrorStore;

    editStore: EditVehicleStore;
    loadingDivisions: FlagStore;
    loadingServiceAccounts: FlagStore;
    loadingGeotabVehicles: FlagStore;
    loadingTags: FlagStore;    
    upsertInProgress: FlagStore;
    upsertErrorStore: ErrorStore;
    upsertController: UpsertVehicleController;

    sensorsSearchCmd: Command2<Key, any>;
    sensorsSearchInitiatorStore: ValueStore<Key>;
}

type Props = TabProps & SearchProps & DeleteItemsProps;
export class VehicleRoute extends React.Component<Props>
{
    private readonly editActions = new EditVehicleActions ();
    
    private readonly vehicleBatchEditActions = new VehicleBatchEditActions();    
    private readonly selectedItemsActions = new SelectedItemsActions();
    private readonly alertActions = new FlagActions();

    private readonly stickyMediator = new StickyMediator();

    componentDidMount()
    {
        this.props.refresher.run();
    }

    render()
    {
        const {
            searchFormStore,
            timezoneStore,
            listStore, selectedItemsStore,
            showVehicleGeopositionCommand,
            mapInProgressStore,
            alertVisibilityStore,
            isAdmin,
            polyglot,
        } = this.props

        const loadingDivisions = [this.props.loadingDivisions, (store: FlagStore) => ({ loadingDivisions: store.isSet() })];
        const loadServiceAccounts = [this.props.loadingServiceAccounts, (store: FlagStore) => ({ loadingServiceAccounts: store.isSet() })];
        const loadGeotabVehicles = [this.props.loadingGeotabVehicles, (store: FlagStore) => ({ loadingVehicles: store.isSet() })];

        const createForm = (props: Partial<EditCreateEntityProps>) => (
            <Container merge stores={twoButtonDialogConfig(this.props.upsertInProgress, this.props.upsertErrorStore)}>
                <EditCreateEntityRoute title={props.title} okTitle={props.okTitle} path={props.path}
                    cancelTitle={polyglot.t('button.cancel')}
                    renderEdit={() => (
                        <Container
                            merge stores={[this.props.editStore, timezoneStore, loadingDivisions, loadServiceAccounts, loadGeotabVehicles]}
                            actions={this.editActions}
                            onMount={this.props.upsertController.preload}>
                            <NewVehicle polyglot={polyglot} loadingDivisions={null} loadingVehicles={null} loadingServiceAccounts={null}
                                onDivisionChanged={this.props.upsertController.setDivision}
                                onServiceAccountChanged={this.props.upsertController.setServiceAccount}
                                onVehicleChanged={this.props.upsertController.setVehicle}
                                />
                        </Container>
                    )}
                    cancel={this.props.upsertController.cancel}
                    ok={this.props.upsertController.upsert}
                />
            </Container>
        );
        const entityType = EntityType.Vehicle;
        const notSelected = () => selectedItemsStore.getState().selected.size === 0;
        const selectionDisableInject = { disabled: notSelected };
        const mapDisableInject = { disabled: () => notSelected() || mapInProgressStore.isSet() }

        return (
            <div>
                {isAdmin && createForm({title: polyglot.t('vehicle.create_title'), okTitle: polyglot.t('button.create'), path: addNewRouteFor(entityType)})}
                {isAdmin && createForm({title: polyglot.t('vehicle.edit_title'), okTitle: polyglot.t('button.save'), path: editRouteFor(entityType)})}
                <DeleteConfirmationWidget {...this.props} entityType={entityType} title={polyglot.t('vehicle.del_confirmation_title')}>
                    <span className={appStyles.ConfirmationContainer}>{polyglot.t('vehicle.del_confirmation_msg')}</span>
                </DeleteConfirmationWidget>
                <Container merge
                    stores={[this.props.groupEditStore, this.props.applyErrorStore]}
                    subscribe={[this.props.applyInProgressStore]}
                    inject={{
                        okDisabled: () => !this.props.groupEditStore.isValid() || this.props.applyInProgressStore.isSet()
                    }}>
                    <EditCreateEntityRoute path={vehicleGroupActionsRoute}
                        title={polyglot.t('vehicle.group_actions')}
                        okTitle={polyglot.t('button.apply')}
                        cancelTitle={polyglot.t('button.cancel')}
                        ok={this.props.applyGroupActionCommand.execute}
                        cancel={() => {
                            this.vehicleBatchEditActions.rejectDraft();
                            appNavigator.goToVehicleTab();
                        }}
                        renderEdit={() => (
                            <Container merge stores={[this.props.groupEditStore, timezoneStore]} actions={this.vehicleBatchEditActions}>
                                <VehicleBatchEdit
                                    polyglot={polyglot}
                                    addedActions={null} timezones={null} unit={null} availableTags={[]} suggestedTags={[]} allTags={[]} isGroupActionValid={null}
                                    axleCount={0}
                                    selectGroupAction={type => this.props.selectGroupActionCommand.execute(type) }/>
                            </Container>
                        )}
                    />                        
                </Container>
                <OneButtonDialog visibilityStore={alertVisibilityStore}
                    title={polyglot.t('misc.info')}
                    okTitle={polyglot.t('button.ok')}
                    ok={() => this.alertActions.reset(alertVisibilityStore.dispatchToken)}>
                        <div>{polyglot.t('vehicle.no_data_msg')}</div>
                </OneButtonDialog>
                <div>
                    <GenericSearchWidget {...this.props} />
                    <div className={appStyles.StickyControlPanel} ref={this.stickyMediator.onToolbox}>
                        <Toolbar>
                            { isAdmin &&
                                <Button type="success" leftIcon="plus" onClick={() => this.props.upsertController.showUpsertForm()}>{this.props.polyglot.t('button.new')}</Button>
                            }
                            <Container inject={selectionDisableInject} stores={[selectedItemsStore]}>
                                <Button leftIcon="pencil" onClick={this.props.showVehicleGroupActionsCommand.execute} >
                                    {polyglot.t('vehicle.group_actions')}
                                </Button>
                            </Container>
                            <NonIsolatedPremise>
                                <Container inject={mapDisableInject} stores={[selectedItemsStore, mapInProgressStore]}>
                                    <Button leftIcon="map-marker"
                                        title={polyglot.t('vehicle.btn_show_on_map')}
                                        onClick={showVehicleGeopositionCommand.execute} />
                                </Container>
                            </NonIsolatedPremise>
                            { isAdmin && <TrashButtonWidget entityType={EntityType.Vehicle} {...this.props} /> }
                            <Container store={this.props.toolbarErrorStore}>
                                <ErrorMessage />
                            </Container>
                        </Toolbar>
                        <TableTitle polyglot={polyglot} labelStore={this.props.tableTitleStore} />
                    </div>
                    <Subscriber channel="nonIsolatedPremise">
                    { nonIsolatedPremise =>
                        <Container merge stores={[
                            listStore, selectedItemsStore,
                            [this.props.sensorsSearchInitiatorStore, (store: ValueStore<Key>) => ({ sensorsSearchInitiator: store.getValue() })]
                        ]}>                        
                            <VehicleGrid
                                className={tableStyles.StickyTable}
                                polyglot={polyglot}
                                isAdmin={isAdmin}
                                isIsolatedPremise={!nonIsolatedPremise}
                                sensorsSearchInitiator={null}
                                items={null}
                                onEdit={(datum, row) => this.props.upsertController.showUpsertForm(datum)}
                                onSensors={(datum: Vehicle, row) => this.props.sensorsSearchCmd.execute(datum.id, { VehicleId: datum.id })}
                                toggle={(datum: Vehicle, row) => this.selectedItemsActions.toggleItem(datum.id, entityType)}
                                toggleAll={() => this.selectedItemsActions.toggleAll(entityType) }
                                onThead={this.stickyMediator.onThead}
                                />
                        </Container>
                    }
                    </Subscriber>
                    <NoResults polyglot={polyglot} listStore={listStore} />
                </div>
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Vehicle/VehicleRoute.tsx